import React, { useRef, useState, useEffect } from "react";
import { useWindowDimensions } from "../../../../../../../common-deprecated/hooks";
import useSlider from "../../../../../../../common-deprecated/hooks/useSlider";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import * as Styles from "../../../../../../../common-deprecated/styles/v2/toyota/custom/TyreSlider";
import Icon from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import * as Button from "../../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import { getTyreLabelsUrl } from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { useCommonSelector } from "../../../../../../../common-deprecated/redux/commonStore";
import type { TyreType } from "../../../../../../../common-deprecated/types/CommonTypes";
import SrOnly from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { BnbLabel } from "../../../../utils/labels";
import type { TyreInfoLabels } from "../../../../../../../shared-logic/utils/tyreInfoContants";

type TyreSliderProps = { tyres: TyreType[]; tyreInfoLabels: TyreInfoLabels };
const DetailedTyreSlider = ({ tyres, tyreInfoLabels }: TyreSliderProps): JSX.Element => {
    const settings = useCommonSelector((state) => state.commonSettings);

    const viewPortRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const windowWidth = useWindowDimensions().width;

    const [viewPortWidth, setViewPortWidth] = useState<number>(0);
    const [tyreLabelError, setTyreLabelError] = useState<boolean>(false);

    useEffect(() => {
        if (viewPortRef.current && sliderRef.current) {
            const { width } = viewPortRef.current.getBoundingClientRect();
            setViewPortWidth(width);
        }
    }, [viewPortWidth, windowWidth]);

    // Retrieve slider props.
    const sliderProps = useSlider(tyres.length * viewPortWidth, viewPortWidth, viewPortWidth);
    const { enableCssTransition, rightArrowEnabled, leftArrowEnabled, rightArrowClickPage, leftArrowClickPage } =
        sliderProps;
    const { startTouchDrag, startMouseDrag, enableDrag, sliderX } = sliderProps;
    const { isScrolling, totalSliderWidth, onTransitionEnd } = sliderProps;

    return (
        <Styles.TyreInfoBody>
            <Styles.SliderButton type="button" show={leftArrowEnabled} alignment="left" onClick={leftArrowClickPage}>
                <SrOnly>
                    <BnbLabel label="bnbA11yButtonSliderPrevious" />
                </SrOnly>
                <Icon variant="chevronLeft" />
            </Styles.SliderButton>

            <Styles.Slider hasSliderButtons>
                <Styles.SliderViewport ref={viewPortRef}>
                    <Styles.Track
                        ref={sliderRef}
                        width={totalSliderWidth}
                        left={enableDrag ? sliderX : 0}
                        enableTransition={enableDrag ? enableCssTransition : false}
                        onTransitionEnd={onTransitionEnd}
                        onTouchStart={enableDrag ? startTouchDrag : undefined}
                        onMouseDown={enableDrag ? startMouseDrag : undefined}
                        onClick={(event) => {
                            // Prevent the click from working when the slider is scrolling.
                            if (isScrolling.current) {
                                event.preventDefault();
                                event.stopPropagation();
                            }
                        }}
                    >
                        {tyres.map((tyre) => (
                            <Styles.ImageDetailedWrapper width={viewPortWidth} key={tyre.id}>
                                <Styles.DetailList>
                                    {tyre.tyreLabels.map((tyreLabel) => (
                                        <>
                                            <dt key={tyre.id + tyreLabel.id + tyreLabel.label}>{tyreLabel.label}</dt>
                                            <dd key={tyre.id + tyreLabel.id + tyreLabel.value}>{tyreLabel.value}</dd>
                                        </>
                                    ))}
                                </Styles.DetailList>
                                <Styles.ImageWrapper>
                                    {!tyreLabelError && (
                                        <Styles.Image
                                            src={getTyreLabelsUrl(settings, tyre.eprelValue)}
                                            alt=""
                                            onError={() => setTyreLabelError(true)}
                                            withoutBorder
                                        />
                                    )}
                                    {tyreLabelError && (
                                        <Flex.Col width={1}>
                                            {tyreInfoLabels.tyreInfoFallbackDescription}
                                            <Button.Link
                                                as="a"
                                                href={`${settings.tyreLabelFallbackService}${tyre.eprelValue}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {tyreInfoLabels.tyreInfoFallback}
                                            </Button.Link>
                                        </Flex.Col>
                                    )}
                                </Styles.ImageWrapper>
                            </Styles.ImageDetailedWrapper>
                        ))}
                    </Styles.Track>
                </Styles.SliderViewport>
            </Styles.Slider>
            <Styles.SliderButton type="button" show={rightArrowEnabled} alignment="right" onClick={rightArrowClickPage}>
                <SrOnly>
                    <BnbLabel label="bnbA11yButtonSliderNext" />
                </SrOnly>
                <Icon variant="chevronRight" />
            </Styles.SliderButton>
        </Styles.TyreInfoBody>
    );
};

export default DetailedTyreSlider;
